import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateFilter, TabButtons } from "react-library";
import dayjs from "dayjs";
import { t } from "../../../services/locale.service";
import { useParams } from "react-router-dom";
import {
  TChartData,
  TCustomProcessFn,
  TDataset,
  TGraphData,
  TTableData,
} from "../types";
import ChartOptionsIcons, {
  TVisualization,
} from "../components/ChartOptionsIcons";
import InformationCardRenderer, {
  TInformationCard,
} from "../components/InformationCardRenderer";
import {
  TFetchSensorDataArguments,
  downloadXLSX,
  fetchAndProcessDiseasePestData,
  fetchAndProcessSensorData,
  generateChartDatasets,
  generateDownloadData,
  generateInformationCardData,
  generateUniqueId,
  getDataType,
  processTableData,
} from "../util";
import DataVisualizationRenderer from "../components/DataVisualizationRenderer";

const SensorBase: React.FC<TProps> = (props) => {
  let { tabButtons } = props;
  const { children, field, unit, diseasePest, customProcessFn, customInformationCards, informationCardWidth } = props;

  const { sensor, plotId } = useParams();

  /**
   * Event Listeners Ids
   */
  const EventId = "sensor-base-date-filter-" + sensor;
  const EventIds = {
    leftArrow: EventId + "-left-arrow",
    rightArrow: EventId + "-right-arrow",
    startDate: EventId + "-start",
    endDate: EventId + "-end",
  };

  const TAB_BUTTONS = {
    day: t("1_Day"),
    week: t("1_Week"),
    month: t("1_Month"),
  };

  if (!tabButtons) {
    tabButtons = TAB_BUTTONS;
  }

  /**
   * States
   */
  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(7, "days").add(330, "minutes").toISOString()
  );
  const [endDate, setEndDate] = useState<string>(
    dayjs().add(330, "minutes").toISOString()
  );
  const [timeFrame, setTimeFrame] = useState<string>("week");
  const timeFrameRef = useRef<string>();

  /**
   * Data states
   */
  const [graphData, setGraphData] = useState<TGraphData>([]);
  const [chartData, setChartData] = useState({
    min: {},
    max: {},
    avg: 0,
    normalRange: {},
  } as TChartData);

  const downloadData = useRef<TGraphData>([]);

  const [tableData, setTableData] = useState<TTableData>([]);
  const [tableColumns, setTableColumns] = useState<Array<string>>([]);

  const [visibleVisualization, setVisibleVisualization] = useState<TVisualization>("graph");

  const [datasets, setDatasets] = useState<TDataset[]>([]);
  const [timestamps, setTimestamps] = useState<Array<string>>([]);
  const [chartUniqueId, setChartUniqueId] = useState<string>(generateUniqueId());
  const [informationCards, setInformationCards] = useState<Array<TInformationCard>>([]);

  /**
   * Event Listeners
   */
  useEffect(() => {
    document.addEventListener("iconButtonClicked", handleCalendarArrowClickEvent);
    document.addEventListener("dateModified", handleDateChangeEvent);
    document.addEventListener("tabButtonClicked", handleTabButtonChangeEvent);

    return () => {
      document.removeEventListener("iconButtonClicked", handleCalendarArrowClickEvent);
      document.removeEventListener("dateModified", handleDateChangeEvent);
      document.removeEventListener("tabButtonClicked", handleTabButtonChangeEvent);
    };
  }, []);

  useEffect(() => {
    timeFrameRef.current = timeFrame;
    updateDateForTimeframe();
  }, [timeFrame]);

  useEffect(() => {
    getGraphData();
  }, [startDate, endDate]);

  useEffect(() => {
    setChartUniqueId(generateUniqueId());
  }, [datasets, timestamps]);

  useEffect(() => {
    generateInformationCardDataWrapper();
  }, [chartData]);

  useEffect(() => {
    generateChartDatasetsWrapper();
  }, [graphData]);

  const getGraphData = async () => {
    if (plotId) {
      if(diseasePest) {
        const res = await fetchAndProcessDiseasePestData({
          startDate,
          endDate,
          plotId,
          diseaseId: sensor!,
          dataFor: diseasePest
        });

        setGraphData(res);
        const tableData = processTableData(res, "disease/pest");
        setTableData(tableData.data);
        setTableColumns(tableData.columns);
      } else {

        /**
         * for sensors like soilMoisture1, VPD, deltaT
         */
        const dataType = getDataType(field, startDate, endDate);

        const args: TFetchSensorDataArguments = {
          plotId,
          startDate,
          endDate,
          dataType,
          field,
        };
  
        if (customProcessFn) {
          args.customProcessFn = customProcessFn;
        }
        
        const { chartData, graphData, data } = await fetchAndProcessSensorData(args);

        setGraphData(graphData);
        setChartData(chartData);

        if (data) {
          const tableData = processTableData(data, field);
          setTableData(tableData.data);
          setTableColumns(tableData.columns);
  
          const downloadData = generateDownloadData(data);
          updateDownloadData(downloadData as TGraphData);
        }
      }
    }
  };
  
  const downloadSensorData = async () => {
    try {
      const { field } = props;
      let csvData = [...downloadData.current!];
  
      const fields = ["to", "gdd", "vpd", "deltaT"];
  
      if (fields.includes(field)) {
        csvData = graphData;
      }
  
      const formattedData = csvData?.map((it) => ({
        ...it,
        date: dayjs(it?.date).format("DD MMM YYYY, hh:mm A"),
      }));
  
      downloadXLSX(formattedData, `${field}-${plotId}-${startDate}-${endDate}`);
     
    } catch (error) {
      console.error(error);
    }
  };

  const viewGallery = async () => {

  };

  /**
   * Event handlers
   */
  const handleCalendarArrowClickEvent = useCallback((e: Event) => {
    const event = e as TArrowClickEvent;
    const { id } = event.detail;

    if (id === "view-graph") {
      setVisibleVisualization("graph");
    } else if (id === "view-table") {
      setVisibleVisualization("table");
    } else if (id === "download-data") {
      downloadSensorData();
    } else if (id === "view-gallery") {
      viewGallery();
    }

    const arrowClicked =
      id === EventIds.leftArrow
        ? "left"
        : id === EventIds.rightArrow
          ? "right"
          : "";

    if (arrowClicked === "left" || arrowClicked === "right") {
      updateDateForTimeframe(arrowClicked);
    }
  }, []);

  const handleDateChangeEvent = useCallback((e: Event) => {
    const event = e as TDateChangeEvent;
    const { id, date } = event.detail;
    if (id === EventIds.startDate) {
      setStartDate(date);
    } else if (id === EventIds.endDate) {
      setEndDate(date);
    }
  }, []);

  const handleTabButtonChangeEvent = useCallback((e: Event) => {
    const event = e as TTabClickEvent;
    const timeFrame = event.detail;
    setTimeFrame(timeFrame);
  }, []);

  /**
   * State handlers
   */
  const updateDownloadData = (newGraphData: TGraphData) => {
    downloadData.current = newGraphData;
  };

  const updateDateForTimeframe = (arrow = "") => {
    if (timeFrameRef.current) {
      let dateDiff = 1;
      switch (timeFrameRef.current) {
      case "day":
        dateDiff = 1;
        break;
      case "week":
        dateDiff = 7;
        break;
      case "month":
        dateDiff = 30;
        break;
      case "year":
        dateDiff = 360;
        break;
      default:
        console.error("time frame not found, switch", timeFrame);
      }

      const newStartDate = dayjs()
        .subtract(dateDiff, "days")
        .add(330, "minutes")
        .toISOString();
      const newEndDate = dayjs().add(330, "minutes").toISOString();

      if (arrow === "left" || arrow === "right") {
        const dateD = arrow === "left" ? -1 * dateDiff : dateDiff;

        setStartDate((prevStartDate) =>
          dayjs(prevStartDate)
            .add(dateD, "days")
            .add(330, "minutes")
            .toISOString()
        );

        setEndDate((prevEndDate) =>
          dayjs(prevEndDate)
            .add(dateD, "days")
            .add(330, "minutes")
            .toISOString()
        );
      } else {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
      }
    }
  };

  const generateChartDatasetsWrapper = () => {
    /**
     * if it's disease pest, then the required data is in `probability` property
     */
    const { datasets, timestamps } = generateChartDatasets(
      graphData,
      field,
      timeFrame!
    );

    setTimestamps(timestamps);
    setDatasets(datasets);
  };

  const generateInformationCardDataWrapper = () => {
    if(diseasePest) {
      return;
    }
    if(customInformationCards) {
      const informationCards = customInformationCards(chartData);
      setInformationCards(informationCards);
      return;
    }

    const informationCards = generateInformationCardData(chartData, field, unit);

    setInformationCards(informationCards);
  };

  return (
    <div>
      <div className="m-y-2 flex content-center items-center">
        <TabButtons buttons={tabButtons} defaultTab="week" />
      </div>

      <div className="m-y-2 flex content-center items-center">
        {timeFrame !== "day" && (
          <DateFilter
            uniqueId={EventId}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>

      <InformationCardRenderer width={informationCardWidth} informationCards={informationCards} />

      <div className="ml-auto width-max-content p-1 relative top-3">
        <ChartOptionsIcons active={visibleVisualization} />
      </div>

      <div >
        <DataVisualizationRenderer
          chartId={chartUniqueId}
          datasets={datasets}
          tableData={tableData}
          tableColumns={tableColumns}
          active={visibleVisualization}
          timestamps={timestamps}
        />
      </div>

      {!!children && children}
    </div>
  );
};

/**
 * ================== TYPES ======================
 */
type TProps = {
  field: string;
  unit: string;
  tabButtons?: Record<string, string>;
  informationCardWidth?: string;
  diseasePest?: "Disease" | "Pest";
  customProcessFn?: TCustomProcessFn;
  customInformationCards?: (chartData: TChartData) => Array<TInformationCard>;
  children?: React.ReactNode;
};

type TTabClickEvent = Event & {
  detail: string;
};

type TDateChangeEvent = Event & {
  detail: {
    id: string;
    date: string;
  };
};

type TArrowClickEvent = Event & {
  detail: {
    id: string;
    name: string;
  };
};

export default SensorBase;
